import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormLayout, FormRow, FormSection, Select, TextInput } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { InfoMessage } from '../../../components/InfoMessage';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { COMPENSATION_TYPE_OPTIONS, emptyCompensationForm } from './constants';
import { CompensationFormSchema, CompensationFormValues } from './types';
import { transformApiToCompensationForm } from './utils';

const TAGS = CT.employer.msaasListings.compensationForm;

export const CompensationForm = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const { compensationStepRef, listing: msaasListing } = useSelector(
    msaasListingsRef,
    (s): MsaasListingContext => {
      if (typeof s === 'object' && s !== null && 'context' in s) {
        return s.context as MsaasListingContext;
      }
      throw new Error('Invalid state: context not found');
    }
  );

  const methods = useCustomForm<CompensationFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(CompensationFormSchema),
    defaultValues: msaasListing?.detail?.id
      ? transformApiToCompensationForm(msaasListing)
      : emptyCompensationForm,
  });

  const {
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (!msaasListing) return;

    // Preserve current form values if the detail is newly created (has id but no compType)
    // This prevents resetting the user's selection when creating a new detail
    const currentValues = methods.getValues();
    const isNewlyCreatedDetail = msaasListing.detail?.id && !msaasListing.detail.compType;

    if (isNewlyCreatedDetail) {
      // Preserve the user's current form values, just update the detail id
      reset({
        ...currentValues,
        id: msaasListing.detail?.id,
        msaasListingId: msaasListing.id,
      });
    } else {
      // Reset to API values for existing details or when no detail exists
      reset(
        msaasListing.detail?.id
          ? transformApiToCompensationForm(msaasListing)
          : {
              ...emptyCompensationForm,
              msaasListingId: msaasListing.id,
            }
      );
    }
  }, [msaasListing, reset]);

  useEffect(() => {
    // Only autofill yearlyRvusPerFte in FTE labor mode.
    // In RVU labor mode, the backend derives this from numRvusReq / numOfFtes.
    if (msaasListing?.detail?.laborReqType !== 'FTE') return;

    const minRVUPerShift = msaasListing?.detail?.minRvusPerShift;
    const totalShifts = msaasListing?.detail?.fteShiftsPerYear;
    if (minRVUPerShift && totalShifts) {
      setValue('yearlyRvusPerFte', minRVUPerShift * totalShifts);
    }
  }, [
    msaasListing?.detail?.laborReqType,
    msaasListing?.detail?.minRvusPerShift,
    msaasListing?.detail?.fteShiftsPerYear,
  ]);

  const compType = watch('compType');

  const onSubmitSuccess = (values: CompensationFormValues) => {
    // Check if we need to save: either form is dirty OR we're creating a new detail (no id yet)
    const needsSave = isDirty || !values.id;

    if (needsSave) {
      // Use ON_ADD if creating a new detail (no id), ON_UPDATE if updating existing
      if (values.id) {
        compensationStepRef.send({ type: 'ON_UPDATE', payload: { values } });
      } else {
        compensationStepRef.send({ type: 'ON_ADD', payload: { values } });
      }
    } else {
      msaasListingsRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    const formState = methods.formState;
    const values = methods.getValues();
    console.error('Compensation Form Submit Error: ', {
      error: err,
      values,
      errors: formState.errors,
      dirtyFields: formState.dirtyFields,
      isDirty: formState.isDirty,
      isValid: formState.isValid,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name={'compType'}
                cyTag={TAGS.compType}
                label={'Compensation Type'}
                options={COMPENSATION_TYPE_OPTIONS}
                required
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              {compType.value === 'HOUR' && (
                <TextInput
                  name={'pricePerHour'}
                  cyTag={TAGS.pricePerHour}
                  label={'Price per Hour'}
                  type="number"
                  required
                />
              )}
              {compType.value === 'SHIFT' && (
                <TextInput
                  name={'pricePerShift'}
                  cyTag={TAGS.pricePerShift}
                  label={'Price per Shift'}
                  type="number"
                  required
                />
              )}
              {compType.value === 'RVU' && (
                <TextInput
                  name={'pricePerRvu'}
                  cyTag={TAGS.pricePerRvu}
                  label={'Price per RVU'}
                  type="number"
                  required
                />
              )}
            </FormRow>
            {compType.value === 'RVU' && (
              <FormRow>
                <TextInput
                  name={'yearlyRvusPerFte'}
                  cyTag={TAGS.yearlyRvusPerFte}
                  label={'Yearly RVUs per FTE'}
                  type="number"
                  required
                />
              </FormRow>
            )}
          </FormSection>
          <InfoMessage
            withOwnMargin={false}
            message={
              'Note: Full compensation calculations including shift differentials will be shown in Step 6: Cost Calculator'
            }
          />
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
